/**
 * Build-time audit
 * ------------------------------------------------------------------
 * Sanity checks that protect the audience boundary. Run at startup
 * for both dev and build modes.
 *
 * Failures here intentionally throw so the dev server won't start
 * and `vite build` will exit non-zero. We'd rather break the build
 * loudly than ship a leaky client bundle quietly.
 */
import { publicDecks } from './publicRegistry';
import { clientSends } from '@/clients/sends';

export function runDeckAudit(): void {
  const errors: string[] = [];

  // 1. Every deck in publicRegistry must self-declare as 'public'.
  for (const deck of publicDecks) {
    if (deck.audience !== 'public') {
      errors.push(
        `[deck-audit] "${deck.id}" is in publicRegistry.ts but its meta declares audience='${deck.audience}'. ` +
          `Either change the meta to 'public' (deliberately) or remove it from publicRegistry.`
      );
    }
  }

  // 2. Every client send must reference a known public deck.
  for (const send of clientSends) {
    const deck = publicDecks.find((d) => d.id === send.deckId);
    if (!deck) {
      errors.push(
        `[deck-audit] Client send "${send.slug}" references deckId "${send.deckId}" ` +
          `which is not in publicRegistry.ts. This is either a typo or an attempt to expose ` +
          `an internal deck — the latter is blocked.`
      );
    }
  }

  // 3. Slugs must be unique.
  const seen = new Set<string>();
  for (const send of clientSends) {
    if (seen.has(send.slug)) {
      errors.push(`[deck-audit] Duplicate client send slug: "${send.slug}".`);
    }
    seen.add(send.slug);
  }

  if (errors.length > 0) {
    throw new Error(
      'Deck audit failed:\n  ' + errors.join('\n  ') + '\n\nFix these before continuing.'
    );
  }
}
